/**
 * Synthesizer base class
 * Abstract interface for converting MIDI notes to audio
 */

// MIDI note related types are commented out (for future expansion)
// import { MidiNote } from '../../../shared/types/midi';

/**
 * ADSR (Attack, Decay, Sustain, Release) envelope settings
 */
export interface ADSREnvelope {
  attack: number;  // seconds
  decay: number;   // seconds
  sustain: number; // 0.0-1.0
  release: number; // seconds
}

/**
 * Synthesizer configuration
 */
export interface SynthesizerConfig {
  envelope?: ADSREnvelope;
  volume?: number; // 0.0-1.0
}

/**
 * Active note information
 */
export interface ActiveNote {
  noteId: string;
  oscillator?: OscillatorNode;
  gainNode: GainNode;
  startTime: number;
  releaseStartTime?: number;
}

/**
 * Synthesizer base class
 */
export abstract class Synthesizer {
  protected context: BaseAudioContext | null = null;
  protected masterGain: GainNode | null = null;
  protected outputNode: AudioNode | null = null; // Output destination node
  protected activeNotes: Map<string, ActiveNote> = new Map();
  protected envelope: ADSREnvelope = {
    attack: 0.01,
    decay: 0.1,
    sustain: 0.7,
    release: 0.3,
  };
  protected volume: number = 0.5;

  constructor(config: SynthesizerConfig = {}) {
    if (config.envelope) {
      this.envelope = { ...this.envelope, ...config.envelope };
    }
    if (config.volume !== undefined) {
      this.volume = config.volume;
    }
  }

  /**
   * Initialize AudioContext
   * @param existingContext Existing AudioContext or OfflineAudioContext
   */
  async initialize(existingContext?: BaseAudioContext): Promise<void> {
    if (this.context) {
      return;
    }

    this.context = existingContext || new AudioContext({ sampleRate: 48000 });

    // OfflineAudioContext does not enter suspended state, so check only for AudioContext
    if (this.context instanceof AudioContext && this.context.state === 'suspended') {
      await this.context.resume();
    }

    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = this.volume;
    // Connect to output node if set, otherwise connect to destination
    if (this.outputNode) {
      this.masterGain.connect(this.outputNode);
    } else {
      this.masterGain.connect(this.context.destination);
    }
  }

  /**
   * Set output destination node
   * Used when connecting to per-track GainNodes
   */
  setOutputNode(node: AudioNode): void {
    this.outputNode = node;
    // If already initialized, reconnect
    if (this.masterGain) {
      this.masterGain.disconnect();
      this.masterGain.connect(node);
    }
  }

  /**
   * Get output destination node
   */
  getOutputNode(): AudioNode | null {
    return this.outputNode;
  }

  /**
   * Get master gain node
   */
  getMasterGain(): GainNode | null {
    return this.masterGain;
  }

  /**
   * Get AudioContext
   */
  getContext(): BaseAudioContext | null {
    return this.context;
  }

  /**
   * Note on (start playing a note)
   */
  abstract noteOn(pitch: number, velocity: number, noteId?: string): string;

  /**
   * Note off (stop playing a note)
   */
  abstract noteOff(noteId: string): void;

  /**
   * Stop all notes
   */
  allNotesOff(): void {
    for (const [noteId] of this.activeNotes) {
      this.noteOff(noteId);
    }
    this.activeNotes.clear();
  }

  /**
   * Convert MIDI note number to frequency
   */
  protected midiNoteToFrequency(note: number): number {
    // A4 (MIDI note 69) = 440Hz
    return 440 * Math.pow(2, (note - 69) / 12);
  }

  /**
   * Apply ADSR envelope
   */
  protected applyADSR(gainNode: GainNode, velocity: number, startTime: number): void {
    if (!this.context) return;

    const now = startTime;
    const velocityGain = velocity / 127;
    const peakGain = velocityGain * 0.8; // Slightly reduce max volume

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(peakGain, now + this.envelope.attack);
    gainNode.gain.linearRampToValueAtTime(
      peakGain * this.envelope.sustain,
      now + this.envelope.attack + this.envelope.decay
    );
  }

  /**
   * Apply release envelope
   */
  protected applyRelease(gainNode: GainNode, releaseTime: number): void {
    if (!this.context) return;

    const currentGain = gainNode.gain.value;
    gainNode.gain.cancelScheduledValues(releaseTime);
    gainNode.gain.setValueAtTime(currentGain, releaseTime);
    gainNode.gain.linearRampToValueAtTime(0, releaseTime + this.envelope.release);
  }

  /**
   * Set volume
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.volume;
    }
  }

  /**
   * Get volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Update envelope settings
   */
  setEnvelope(envelope: Partial<ADSREnvelope>): void {
    this.envelope = { ...this.envelope, ...envelope };
  }

  /**
   * Get envelope settings
   */
  getEnvelope(): ADSREnvelope {
    return { ...this.envelope };
  }

  /**
   * Replace masterGain with a new one
   * Completely isolates zombie voices connected to the old masterGain
   * (e.g., voices that escaped tracking via noteOff's setTimeout),
   * making audio output impossible for them.
   * After calling this, the new masterGain must be connected externally
   * (via connectSynthToTrack, etc.).
   */
  replaceMasterGain(): void {
    if (!this.context) return;

    // Disconnect old masterGain (lose its output destination)
    if (this.masterGain) {
      try { this.masterGain.disconnect(); } catch (e) {}
    }

    // Create new masterGain (not connected anywhere yet)
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = this.volume;
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.allNotesOff();

    if (this.masterGain) {
      this.masterGain.disconnect();
      this.masterGain = null;
    }

    // Do not close externally provided context
    this.context = null;
  }
}

/**
 * Generate a unique ID
 */
export function generateNoteId(): string {
  return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
